import React, { useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Read reCAPTCHA site key from environment variable
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MHBiELhP';

/**
 * CaptchaModal — A reusable MUI Dialog that presents a reCAPTCHA v2 challenge.
 *
 * Props:
 *  - open (bool): Whether the dialog is visible
 *  - onClose (() => void): Called when the user dismisses the modal
 *  - onVerify ((token: string) => void): Called once the CAPTCHA is solved
 */
const CaptchaModal = ({ open, onClose, onVerify }) => {
  const recaptchaRef = useRef(null);

  const handleCaptchaChange = (token) => {
    if (token) {
      onVerify(token);
      // Reset the widget so it can be used again on the next submission
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  const handleClose = () => {
    // Reset reCAPTCHA when closing without completing
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 0,
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          Verify you're human
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          size="small"
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          Please complete the CAPTCHA below to submit your form.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            '& > div': {
              transform: { xs: 'scale(0.85)', sm: 'scale(1)' },
              transformOrigin: 'center',
            },
          }}
        >
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" size="small">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaptchaModal;
